// Batch sync (§5, §11.5): which part of Settings each SyncGroup covers, and how to copy groups from a
// source photo onto another. `background` syncs mode, key, edge, fill and shadow — never the refine
// strokes or the cached mask (§5); `crop` covers the crop rect, aspect and angle; `perspective` the
// keystone sliders.
import type { Settings, SyncGroup } from '../model/types';

const clone = <T>(v: T): T => structuredClone(v);

/** Copies the chosen groups from `src` onto a copy of `dst`. */
export function applyGroups(dst: Settings, src: Settings, groups: readonly SyncGroup[]): Settings {
  const out = clone(dst);
  for (const g of groups) {
    switch (g) {
      case 'light':
      case 'wb':
      case 'color':
      case 'hsl':
      case 'curve':
      case 'detail':
      case 'masks':
      case 'heal':
      case 'skin':
      case 'cleanWhites':
      case 'lut':
      case 'effects':
        (out as Record<string, unknown>)[g] = clone(src[g]);
        break;
      case 'crop':
        out.geometry.crop = clone(src.geometry.crop);
        out.geometry.aspect = src.geometry.aspect;
        out.geometry.angle = src.geometry.angle;
        out.geometry.constrain = src.geometry.constrain;
        break;
      case 'perspective':
        out.geometry.vertical = src.geometry.vertical;
        out.geometry.horizontal = src.geometry.horizontal;
        break;
      case 'background': {
        const { refine: _refine, maskCacheKey: _key, ...rest } = clone(src.background);
        // Mode/edge/fill/shadow/key come over; the destination keeps its own refine strokes and its
        // own mask cache key (its mask is computed from ITS pixels).
        out.background = {
          ...out.background,
          ...rest,
          refine: out.background.refine,
          maskCacheKey: dst.background.maskCacheKey,
        };
        break;
      }
    }
  }
  return out;
}

/** The subset of settings a preset stores for the chosen groups (geometry/background as partials). */
export function extractGroups(src: Settings, groups: readonly SyncGroup[]): Partial<Settings> {
  const base = applyGroups(structuredClone(src), src, groups);
  const out: Partial<Settings> = {};
  for (const g of groups) {
    if (g === 'crop' || g === 'perspective') out.geometry = base.geometry;
    else if (g === 'background') out.background = { ...base.background, refine: [] };
    else (out as Record<string, unknown>)[g] = (base as Record<string, unknown>)[g];
  }
  return out;
}

/** Applies a (possibly partial) preset settings object over a photo's settings. */
export function applyPartial(
  dst: Settings,
  patch: Partial<Settings>,
  groups: readonly SyncGroup[],
): Settings {
  const src = structuredClone(dst);
  for (const [k, v] of Object.entries(patch)) (src as Record<string, unknown>)[k] = structuredClone(v);
  return applyGroups(dst, src, groups);
}
